import { Link, useLocation } from "react-router-dom";
import {
    Phone,
    Mail,
    MapPin,
    Clock,
    Facebook,
    Instagram,
    Youtube,
    ShieldCheck,
} from "lucide-react";
import { Container } from "@/components/ui/container";
import LogoFull from "@/components/ui/LogoFull";
import { COMPANY, SOCIAL_LINKS } from "@/lib/constants";
import { BUSINESS_ADDRESS_LINES, EXTERNAL_PROFILES } from "@/lib/seo-constants";
import { TRACK_LIST, getTrack } from "@/lib/tracks";

const SOCIAL_ICONS = {
    Facebook,
    Instagram,
    Youtube,
};

const ColumnTitle = ({ children }) => (
    <h4 className="font-display text-xl tracking-wider text-white mb-5">
        {children}
    </h4>
);

const Footer = () => {
    const { pathname } = useLocation();
    const track = getTrack(pathname);

    return (
    <footer
        className="w-full bg-brand-dark text-white/80"
        data-testid="site-footer"
    >
        <Container className="py-14 md:py-20">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-12">
                {/* Company */}
                <div data-testid="footer-col-company">
                    <Link
                        to="/"
                        className="inline-flex items-center mb-4 group"
                    >
                        <LogoFull theme="dark" />
                    </Link>
                    <p className="text-sm leading-relaxed text-white/70 mb-5">
                        {COMPANY.tagline}
                    </p>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-brand-cyan mb-5">
                        <ShieldCheck className="h-4 w-4" strokeWidth={2.25} />
                        Licensed & Insured
                    </div>

                    {/* Official BBB Accredited Business seal. The image is served
                        live from BBB so it always reflects current accreditation
                        status — do not self-host it. rel="nofollow" is required by
                        BBB's seal terms; noopener/noreferrer are added for safety.
                        Explicit width/height reserve the space so the late-loading
                        external image can't shift the footer (CLS). */}
                    <a
                        href={`${EXTERNAL_PROFILES.bbb}/#sealclick`}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="inline-block mb-5"
                        data-testid="footer-bbb-seal"
                    >
                        <img
                            src="https://seal-mwco.bbb.org/seals/blue-seal-200-42-bbb-1411380.png"
                            alt="Reformity Inc. BBB Business Review"
                            width={200}
                            height={42}
                            loading="lazy"
                            style={{ border: 0 }}
                        />
                    </a>
                    {/* Cross-track links */}
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">
                        Explore
                    </p>
                    <ul className="flex flex-wrap gap-x-3 gap-y-1">
                        {TRACK_LIST.map((t) => (
                            <li key={t.key}>
                                <Link
                                    to={t.home}
                                    className="text-xs font-semibold text-white/70 hover:text-brand-cyan transition-colors"
                                    data-testid={`footer-track-${t.key}`}
                                >
                                    {t.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Services (current track) */}
                <div data-testid="footer-col-services">
                    <ColumnTitle>{track.label} Services</ColumnTitle>
                    <ul className="space-y-2.5">
                        {track.services.map((s) => (
                            <li key={s.path}>
                                <Link
                                    to={s.path}
                                    className="text-sm text-white/70 hover:text-brand-cyan transition-colors duration-200"
                                    data-testid={`footer-service-${s.path.split("/").pop()}`}
                                >
                                    {s.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Quick Links */}
                <div data-testid="footer-col-quicklinks">
                    <ColumnTitle>Quick Links</ColumnTitle>
                    <ul className="space-y-2.5">
                        <li>
                            <Link
                                to={track.home}
                                className="text-sm text-white/70 hover:text-brand-cyan transition-colors duration-200"
                            >
                                {track.label} Home
                            </Link>
                        </li>
                        {track.navLinks.map((link) => (
                            <li key={link.path}>
                                <Link
                                    to={link.path}
                                    className="text-sm text-white/70 hover:text-brand-cyan transition-colors duration-200"
                                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                        <li>
                            <Link
                                to="/reviews"
                                className="text-sm text-white/70 hover:text-brand-cyan transition-colors duration-200"
                                data-testid="footer-link-reviews"
                            >
                                Reviews
                            </Link>
                            <p
                                className="mt-1 text-[10px] uppercase tracking-wider text-white/40"
                                data-testid="footer-reviews-platforms"
                            >
                                Verified on{" "}
                                <Link
                                    to="/reviews/google"
                                    className="hover:text-brand-cyan transition-colors"
                                    data-testid="footer-reviews-platform-google"
                                >
                                    Google
                                </Link>
                                <span className="text-white/25"> · </span>
                                <Link
                                    to="/reviews/homestars"
                                    className="hover:text-brand-cyan transition-colors"
                                    data-testid="footer-reviews-platform-homestars"
                                >
                                    Homestars
                                </Link>
                                <span className="text-white/25"> · </span>
                                <Link
                                    to="/reviews/bark"
                                    className="hover:text-brand-cyan transition-colors"
                                    data-testid="footer-reviews-platform-bark"
                                >
                                    Bark
                                </Link>
                                <span className="text-white/25"> · </span>
                                <Link
                                    to="/reviews/houzz"
                                    className="hover:text-brand-cyan transition-colors"
                                    data-testid="footer-reviews-platform-houzz"
                                >
                                    Houzz
                                </Link>
                                <span className="text-white/25"> · </span>
                                <Link
                                    to="/reviews/bbb"
                                    className="hover:text-brand-cyan transition-colors"
                                    data-testid="footer-reviews-platform-bbb"
                                >
                                    BBB
                                </Link>
                            </p>
                        </li>
                    </ul>
                </div>

                {/* Contact */}
                <div data-testid="footer-col-contact">
                    <ColumnTitle>Contact</ColumnTitle>
                    <ul className="space-y-3 text-sm">
                        <li>
                            <a
                                href={COMPANY.phoneHref}
                                className="group flex items-start gap-3 text-white/80 hover:text-brand-cyan transition-colors duration-200"
                                data-testid="footer-phone-link"
                            >
                                <Phone
                                    className="h-4 w-4 mt-0.5 text-brand-coral shrink-0"
                                    strokeWidth={2.25}
                                />
                                <span>{COMPANY.phone}</span>
                            </a>
                        </li>
                        <li>
                            <a
                                href={COMPANY.emailHref}
                                className="group flex items-start gap-3 text-white/80 hover:text-brand-cyan transition-colors duration-200"
                                data-testid="footer-email-link"
                            >
                                <Mail
                                    className="h-4 w-4 mt-0.5 text-brand-coral shrink-0"
                                    strokeWidth={2.25}
                                />
                                <span>{COMPANY.email}</span>
                            </a>
                        </li>
                        <li className="flex items-start gap-3 text-white/70">
                            <MapPin
                                className="h-4 w-4 mt-0.5 text-brand-coral shrink-0"
                                strokeWidth={2.25}
                            />
                            <span data-testid="footer-address">
                                {BUSINESS_ADDRESS_LINES.map((line, i) => (
                                    <span key={i} className="block">
                                        {line}
                                    </span>
                                ))}
                            </span>
                        </li>
                        <li className="flex items-start gap-3 text-white/70">
                            <Clock
                                className="h-4 w-4 mt-0.5 text-brand-coral shrink-0"
                                strokeWidth={2.25}
                            />
                            <span>{COMPANY.hours}</span>
                        </li>
                    </ul>

                    <div className="mt-6 flex items-center gap-3" data-testid="footer-socials">
                        {SOCIAL_LINKS.map((s) => {
                            const Icon = SOCIAL_ICONS[s.icon];
                            return (
                                <a
                                    key={s.label}
                                    href={s.href}
                                    aria-label={s.label}
                                    className="flex h-9 w-9 items-center justify-center border border-white/15 text-white/80 hover:text-white hover:border-brand-cyan hover:bg-brand-cyan transition-all duration-200"
                                    data-testid={`footer-social-${s.label.toLowerCase()}`}
                                >
                                    <Icon className="h-4 w-4" strokeWidth={2.25} />
                                </a>
                            );
                        })}
                    </div>
                </div>
            </div>
        </Container>

        <div className="border-t border-white/10">
            <Container className="py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-white/50">
                <p data-testid="footer-copyright">
                    © {new Date().getFullYear()} {COMPANY.name} All rights reserved.
                </p>
                <div className="flex items-center gap-4">
                    <Link
                        to="/privacy"
                        className="hover:text-brand-cyan transition-colors duration-200"
                        data-testid="footer-link-privacy"
                    >
                        Privacy Policy
                    </Link>
                    <span className="uppercase tracking-wider">Licensed &amp; Insured</span>
                </div>
            </Container>
        </div>
    </footer>
    );
};

export default Footer;
